import React, { FormEvent, KeyboardEvent, useRef, useState } from 'react';
import Autosuggest, { GetSuggestionValue, SuggestionSelectedEventData } from 'react-autosuggest';
import _isEmpty from 'lodash/isEmpty';
import cx from 'classnames';
import { toastsActions } from '@zola-helpers/client/dist/es/redux/toasts';

import type { WMarketplaceLocationEntity } from '@zola-helpers/client/dist/es/api/marketplace';
import type { USCityView } from '@zola/svc-marketplace-ts-types';
import type { WBudgetItemTypeView, WBudgetView } from '@zola/svc-web-api-ts-client';

import { setCookie, deleteCookie } from '@zola-helpers/client/dist/es/util/storage';
import { isDesktopV2 } from '@zola-helpers/client/dist/es/util/responsive';
import pluralize from '@zola-helpers/client/dist/es/transformers/pluralize';

import Modal, { ModalV2Props } from '@zola/zola-ui/src/components/Modal/ModalV2';
import { Title2 } from '@zola/zola-ui/src/typography/Headings';
import { BodySmall } from '@zola/zola-ui/src/typography/Paragraphs';
import { ButtonV3 } from '@zola/zola-ui/src/components/ButtonV3';
import InputFieldV3 from '@zola/zola-ui/src/components/Form/inputV3/InputFieldV3';
import { InputFieldAddOnType } from '@zola/zola-ui/src/components/Form/InputFieldV2/InputFieldAddOn';
import {
  debouncedFetchSuggestedLocations,
  parseCityState,
} from '@zola/zola-ui/src/components/OnboardV2/common/steps/WeddingLocationStep/helpers';
import useEffectOnce from '@zola/zola-ui/src/hooks/useEffectOnce';

import bellCurveLine from 'assets/images/budget/bell-curve-line.svg';
import bellCurveShadow from 'assets/images/budget/bell-curve-shadow.svg';

import { getUserContext as fetchUserContext } from 'actions/UserActions';
import {
  fetchSuggestedBudgetTotal,
  getAccountVendors,
  getMarketplaceLocation,
  MappedWAccountVendorView,
  updateWeddingLocation,
} from 'api/vendorMarketplaceApi';
import { useAppDispatch } from 'reducers/useAppDispatch';
import { useAppSelector } from 'reducers/useAppSelector';
import {
  getUserContext,
  getUserWeddingCity,
  getUserWeddingGuestCount,
  getUserWeddingStateProvince,
} from 'selectors/user/userSelectors';
import { handleBudgetRouteChange } from '../routes/handleBudgetRouteChange';
import { createBudget, getBudgetItemTypes, updateBudget } from '../util/api';
import { mapCostToFormattedString } from '../util/mappers';
import { useCurrencyInput } from '../util/useCurrencyInput';
import { useBudgetContext } from '../context';

import {
  Container,
  HeadingContainer,
  Dek,
  Flex,
  FormFields,
  GapControl,
  AutosuggestContainer,
  StyledSkipBtn,
  FormLabel,
  AssetContainer,
  Animation,
  BellCurveLine,
  BellCurveShadow,
  GraphLine,
  GraphLineContainer,
  GraphLineText,
  GraphText,
  NoWhiteSpace,
  GuestCount,
  Total,
} from './BudgetDetailsModal.styles';

type BudgetDetailsModalProps = Pick<ModalV2Props, 'triggerRef'> & {
  onClose?: () => void;
};

const BudgetDetailsModal = ({ onClose, triggerRef }: BudgetDetailsModalProps) => {
  const dialogHeadingId = 'budget-onboarding-modal';
  const dispatch = useAppDispatch();
  const { budget: hasBudget } = useAppSelector(getUserContext);
  const userCity = useAppSelector(getUserWeddingCity);
  const userStateProvince = useAppSelector(getUserWeddingStateProvince);
  const {
    actions: {
      receiveBudget,
      receiveItemTypes,
      receiveAccountVendors,
      receiveBusyState,
      receiveMarketplaceLocation,
    },
    state: {
      budget: { budgeted_cents: budgetedCents, uuid: budgetUuid },
      marketplaceLocation: { city: marketplaceCity, stateProvince, citySlug },
    },
  } = useBudgetContext();

  const [weddingCity, setWeddingCity] = useState<string | undefined>(userCity || marketplaceCity);
  const [weddingStateProvince, setWeddingStateProvince] = useState<string | undefined>(
    userStateProvince || stateProvince
  );
  const [weddingGuestCount, setWeddingGuestCount] = useState<number | undefined>(
    useAppSelector(getUserWeddingGuestCount)
  );
  const [suggestedAvgTotal, setSuggestedAvgTotal] = useState<number | undefined>();
  const [suggestedGuestCount, setSuggestedGuestCount] = useState<number | undefined>();

  const [suggestedLocations, setSuggestedLocations] = useState<WMarketplaceLocationEntity[]>([]);
  const autoSuggestElement = useRef<HTMLInputElement | null>(null);
  const inputRef = useRef<HTMLInputElement | null>(null);
  const [inputValue, setInputValue] = useState<string>(`${weddingCity}, ${weddingStateProvince}`);

  const {
    currencyValue,
    rawCurrencyValue,
    setCurrencyValue,
    toggleIsEditingCurrency,
  } = useCurrencyInput(((budgetedCents || 0) / 100).toFixed(2), inputRef, []);

  const handleFetchSuggestedBudgetTotal = async (newSlug?: string) => {
    const { budgetEstimateMidPointCents, guestCount } = await fetchSuggestedBudgetTotal({
      ...(citySlug && { location: newSlug || citySlug }),
      ...(weddingGuestCount && { guest_count: weddingGuestCount }),
    });
    setSuggestedAvgTotal(budgetEstimateMidPointCents);
    setSuggestedGuestCount(guestCount);
  };

  useEffectOnce(() => {
    handleFetchSuggestedBudgetTotal().catch(() => undefined);
  });

  // --------------------------------------------------------------------------
  // Begin Auto-Suggest
  // --------------------------------------------------------------------------
  const onSuggestionSelected = (
    event: FormEvent,
    { suggestion: location }: SuggestionSelectedEventData<WMarketplaceLocationEntity>
  ): void => {
    event.preventDefault();
    const { type, name, slug } = location;

    if (type === 'US_CITY' || type === 'NOT_LISTED') {
      deleteCookie('citySlug');
      if (slug) {
        setCookie('citySlug', slug, 1);
        handleFetchSuggestedBudgetTotal(slug).catch(() => undefined);
      }
      if (type === 'US_CITY') {
        const { city = '', state_province = '' } = parseCityState(name) || {};
        setWeddingCity(city);
        setWeddingStateProvince(state_province);
        updateWeddingLocation({ location: { name: city, stateCode: state_province } as USCityView })
          .then(getMarketplaceLocation)
          .then(res => {
            if (res && !_isEmpty(res)) receiveMarketplaceLocation(res);
          })
          .catch(() => undefined);
      }
    } else if (autoSuggestElement.current) {
      autoSuggestElement.current.focus();
    }

    const suggestEl = document?.getElementById('location-autosuggest');
    if (location.type !== 'PLACEHOLDER' && suggestEl) {
      suggestEl.blur();
    }
  };

  // Used as the value for the input
  const getSuggestionValue: GetSuggestionValue<WMarketplaceLocationEntity> = suggestion => {
    if (suggestion?.type === 'US_CITY' || suggestion?.type === 'NOT_LISTED') {
      return suggestion.name;
    }
    return '';
  };

  // Used in render suggestion
  const getSuggestionText = (location: WMarketplaceLocationEntity): string | null => {
    if (location.type === 'PLACEHOLDER') {
      return location.name;
    }
    return getSuggestionValue(location);
  };
  const handleEnterKeyPress = (event: KeyboardEvent): void => {
    if (event.key === 'Enter') {
      event.preventDefault();
      const suggestion = suggestedLocations && suggestedLocations[0];
      if (suggestion) {
        onSuggestionSelected(event, {
          suggestion,
          suggestionValue: suggestion.name,
          suggestionIndex: 0,
          sectionIndex: 0,
          method: 'enter',
        });
      }
      const suggestEl = document?.getElementById('location-autosuggest');
      if (suggestEl) suggestEl.blur();
    }
  };

  const renderSuggestion = (location: WMarketplaceLocationEntity): JSX.Element => {
    const classes = cx('auto-suggestion-container', {
      'auto-suggestion-container-placeholder': location.type === 'PLACEHOLDER',
    });

    return <div className={classes}>{getSuggestionText(location)}</div>;
  };
  // --------------------------------------------------------------------------
  // End Auto-Suggest
  // --------------------------------------------------------------------------

  const handleGuestCountChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setWeddingGuestCount(Number(e.target.value));
  };

  const handleCreateOrUpdateBudget = () => {
    const values = {
      wedding_city: weddingCity,
      wedding_state_province: weddingStateProvince,
      wedding_guest_count: weddingGuestCount,
      budgeted_cents: Number(rawCurrencyValue) * 100,
    };
    Promise.all([
      hasBudget
        ? updateBudget({ ...values, budget_uuid: budgetUuid as string })
        : createBudget({ ...values, origin: 'BUDGET_TOOL' }),
      getBudgetItemTypes(),
      getAccountVendors(),
    ])
      .then((responses: [WBudgetView, WBudgetItemTypeView[], MappedWAccountVendorView[]]) => {
        const [budget, itemTypes, accountVendors] = responses;
        receiveBudget(budget);
        receiveItemTypes(itemTypes);
        receiveAccountVendors(accountVendors);
        receiveBusyState(false);
      })
      .then(() => {
        if (hasBudget) {
          dispatch(
            toastsActions.positive({
              headline: 'Updated estimates',
            })
          );
        }
        onClose?.();
        dispatch(fetchUserContext()).catch(() => undefined);
        handleBudgetRouteChange();
      })
      .catch(() => {
        dispatch(
          toastsActions.negative({
            headline:
              'There was an error creating your budget. Please refresh the page or contact customer service.',
          })
        );
      });
  };

  const handleSkip = () => {
    handleCreateOrUpdateBudget();
    onClose?.();
  };

  const handleClose = () => {
    if (hasBudget) onClose?.();
    else handleSkip();
  };

  return (
    <Modal
      onClose={handleClose}
      rootId="budget-modal-root"
      triggerRef={triggerRef}
      dialogHeadingId={dialogHeadingId}
    >
      <Container>
        {isDesktopV2() ? (
          <HeadingContainer>
            <Title2 presentation="h3">Let us help you plan your budget</Title2>
            <Dek color="BLACK_075">
              We&apos;ll provide estimated costs and recommendations based on other couples in your
              area with a similar budget.
            </Dek>
          </HeadingContainer>
        ) : (
          <HeadingContainer>
            <Title2 presentation="h3">{budgetedCents ? 'Setup' : 'Update'} your budget</Title2>
            <Dek color="BLACK_075">You can always change these later.</Dek>
          </HeadingContainer>
        )}
        <Flex>
          <FormFields>
            {isDesktopV2() && (
              <FormLabel htmlFor="location-autosuggest">Wedding location</FormLabel>
            )}
            <GapControl>
              <AutosuggestContainer>
                {!isDesktopV2() && (
                  <FormLabel htmlFor="location-autosuggest">Wedding location</FormLabel>
                )}
                <Autosuggest<WMarketplaceLocationEntity>
                  suggestions={suggestedLocations}
                  onSuggestionSelected={onSuggestionSelected}
                  onSuggestionsFetchRequested={({ value }): void =>
                    debouncedFetchSuggestedLocations(value, setSuggestedLocations)
                  }
                  onSuggestionsClearRequested={(): void => setSuggestedLocations([])}
                  getSuggestionValue={getSuggestionValue}
                  renderSuggestion={renderSuggestion}
                  inputProps={{
                    id: 'location-autosuggest',
                    value: inputValue || '',
                    autoComplete: 'off',
                    autoFocus: false,
                    placeholder: 'City, State',
                    ref: autoSuggestElement,
                    required: true,
                    onKeyUp: handleEnterKeyPress,
                    onChange: (_e, { newValue }): void => {
                      setInputValue(newValue);
                    },
                  }}
                />
              </AutosuggestContainer>
              <GuestCount>
                <InputFieldV3
                  name="weddingGuestCount"
                  value={(weddingGuestCount || '').toString()}
                  label="Guest count"
                  type="number"
                  isControlled
                  onChange={handleGuestCountChange}
                  onBlur={() => {
                    handleFetchSuggestedBudgetTotal().catch(() => undefined);
                  }}
                />
              </GuestCount>
              <Total>
                <InputFieldV3
                  name="budgetedCents"
                  label="Total budget"
                  tooltip="The ceiling of your budget—AKA the amount you'd prefer not to go beyond."
                  addOn={InputFieldAddOnType.CASH}
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                    setCurrencyValue(e.target.value)
                  }
                  onFocus={toggleIsEditingCurrency}
                  onBlur={toggleIsEditingCurrency}
                  value={currencyValue}
                  isControlled
                  ref={inputRef}
                />
              </Total>
              {isDesktopV2() && (
                <BodySmall color="BLACK_075">You can always change these later</BodySmall>
              )}
            </GapControl>
          </FormFields>
          <AssetContainer>
            {suggestedGuestCount && (
              <GraphText>
                Total budget from couples in{' '}
                <NoWhiteSpace>
                  {weddingCity}, {weddingStateProvince}
                </NoWhiteSpace>{' '}
                with {pluralize('guest', 'guests', suggestedGuestCount)}:
              </GraphText>
            )}
            <Animation>
              <BellCurveLine src={bellCurveLine} alt="" />
              <BellCurveShadow src={bellCurveShadow} alt="" />
              <GraphLineContainer suggestedCents={suggestedAvgTotal || 0}>
                {[...Array(30)].map((_, i) => (
                  <GraphLine key={`graph-line-${i}`}>
                    <GraphLineText>{mapCostToFormattedString(i * 1000000, true)}</GraphLineText>
                  </GraphLine>
                ))}
              </GraphLineContainer>
            </Animation>
          </AssetContainer>
        </Flex>
        <ButtonV3 type="submit" fullWidth size="large" onClick={handleCreateOrUpdateBudget}>
          {hasBudget ? 'Update' : 'Next'}
        </ButtonV3>
        {!hasBudget && (
          <StyledSkipBtn role="button" onClick={handleSkip} type="button" sizes="smaller" inline>
            Skip for now
          </StyledSkipBtn>
        )}
      </Container>
    </Modal>
  );
};

export default BudgetDetailsModal;
